import {
	CreateWebhookResult,
	TriggerWebhookTestPayload,
	TriggerWebhookTestResult,
	WebhookDto,
	WebhookEvent,
	WebhookDeliveryList,
} from "@dune/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axios";

const webhookKeys = {
	all: ["webhooks"] as const,
	details: () => [...webhookKeys.all, "details"] as const,
	detail: (id: number) => [...webhookKeys.details(), id] as const,
	list: () => [...webhookKeys.all, "list"] as const,
	deliveries: (page: number, limit: number) =>
		[...webhookKeys.all, "deliveries", page, limit] as const,
};

export interface UpsertWebhookPayload {
	name: string;
	url: string;
	description?: string | null;
	isActive?: boolean;
	events: WebhookEvent[];
}

export interface UpdateWebhookPayload extends UpsertWebhookPayload {
	id: number;
}

const fetchWebhooks = async (): Promise<WebhookDto[]> => {
	const { data } = await privateAxios.get<WebhookDto[]>("/webhooks");
	return data;
};

export const useWebhooksQuery = () => {
	return useQuery({
		queryKey: webhookKeys.list(),
		queryFn: fetchWebhooks,
	});
};

const createWebhook = async (
	payload: UpsertWebhookPayload,
): Promise<CreateWebhookResult> => {
	const { data } = await privateAxios.post<CreateWebhookResult>(
		"/webhooks",
		payload,
	);
	return data;
};

export const useCreateWebhookMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createWebhook,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: webhookKeys.list() });
		},
	});
};

const updateWebhook = async (
	payload: UpdateWebhookPayload,
): Promise<WebhookDto> => {
	const { id, ...body } = payload;
	const { data } = await privateAxios.patch<WebhookDto>(
		`/webhooks/${id}`,
		body,
	);
	return data;
};

export const useUpdateWebhookMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateWebhook,
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: webhookKeys.list() });
			queryClient.invalidateQueries({
				queryKey: webhookKeys.detail(variables.id),
			});
		},
	});
};

const triggerWebhookTest = async (
	payload: TriggerWebhookTestPayload,
): Promise<TriggerWebhookTestResult> => {
	const { data } = await privateAxios.post<TriggerWebhookTestResult>(
		"/webhooks/test",
		payload,
	);
	return data;
};

export const useTriggerWebhookTestMutation = () => {
	return useMutation({
		mutationFn: triggerWebhookTest,
	});
};

const fetchWebhookDeliveries = async (params: {
	page: number;
	limit: number;
}): Promise<WebhookDeliveryList> => {
	const { data } = await privateAxios.get<WebhookDeliveryList>(
		"/webhooks/deliveries",
		{ params },
	);
	return data;
};

export const useWebhookDeliveriesQuery = (
	page: number,
	limit: number,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: webhookKeys.deliveries(page, limit),
		queryFn: () => fetchWebhookDeliveries({ page, limit }),
		placeholderData: (previousData) => previousData,
		enabled: options?.enabled,
	});
};
